import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import Markdown from "../components/Markdown";
import MarkdownEditor from "../components/MarkdownEditor";
import SentimentChip from "../components/SentimentChip";
import {
  ArrowLeft,
  ArrowBigUp,
  Flag,
  Trash2,
  Send,
  MessageCircle,
  Pin,
  PinOff,
} from "lucide-react";

export default function ThreadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [tRes, rRes] = await Promise.all([
        api.get(`/threads/${id}`),
        api.get(`/threads/${id}/replies`),
      ]);
      setThread(tRes.data);
      setReplies(rRes.data);
    } catch (e) { setError(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const voteThread = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    const { data } = await api.post("/vote", { target_type: "thread", target_id: id });
    setThread((t) => ({ ...t, upvotes: data.count, has_voted: data.voted }));
  };

  const voteReply = async (rid) => {
    if (!user || user === false) { navigate("/login"); return; }
    const { data } = await api.post("/vote", { target_type: "reply", target_id: rid });
    setReplies((list) => list.map((r) => r.id === rid ? { ...r, upvotes: data.count, has_voted: data.voted } : r));
  };

  const togglePin = async () => {
    try {
      await api.post(`/threads/${id}/pin`, { pinned: !thread.pinned });
      setThread((t) => ({ ...t, pinned: !t.pinned }));
    } catch (e) { alert(formatApiError(e)); }
  };

  const postReply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/threads/${id}/replies`, { body: body.trim() });
      setBody("");
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const reportItem = async (target_type, target_id) => {
    if (!user || user === false) { navigate("/login"); return; }
    const reason = window.prompt("Why are you reporting this? (at least 3 characters)");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post("/reports", { target_type, target_id, reason: reason.trim() });
      alert("Reported. A moderator will review it soon.");
    } catch (e) { alert(formatApiError(e)); }
  };

  const deleteThread = async () => {
    if (!window.confirm("Delete this thread?")) return;
    try {
      await api.delete(`/threads/${id}`);
      navigate(`/chapters/${thread.chapter_number}`);
    } catch (e) { alert(formatApiError(e)); }
  };

  const deleteReply = async (rid) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/replies/${rid}`);
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  if (!thread) {
    return (
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
        {error ? (
          <div className="text-sm text-[#C05B3B]">{error}</div>
        ) : (
          <>
            <div className="h-6 w-36 bg-[#F3EFE9] rounded animate-pulse" />
            <div className="mt-6 h-10 w-2/3 bg-[#F3EFE9] rounded animate-pulse" />
          </>
        )}
      </div>
    );
  }

  const canDeleteThread = user && user !== false && (user.role === "admin" || user.id === thread.author?.id);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-14" data-testid="thread-detail-page">
      <Link
        to={`/chapters/${thread.chapter_number}`}
        className="inline-flex items-center gap-2 text-sm text-[#5C6B5F] hover:text-[#D96C4A]"
        data-testid="back-to-chapter"
      >
        <ArrowLeft size={16} /> Back to Chapter {thread.chapter_number}
      </Link>

      <article className="mt-6 animate-fade-up">
        <div className="flex items-center gap-2 flex-wrap">
          {thread.pinned && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#C05B3B] bg-[#F2D8CF] px-2 py-0.5 rounded-full" data-testid="thread-detail-pinned">
              <Pin size={10} /> Pinned
            </span>
          )}
          {thread.sentiment && <SentimentChip sentiment={thread.sentiment} size="xs" />}
        </div>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium text-[#2D3B31] leading-tight mt-2" data-testid="thread-title">
          {thread.title}
        </h1>
        <div className="text-sm text-[#8A948C] mt-2">
          by <span className="text-[#5C6B5F]">{thread.author?.name}</span>
          {" · "}
          {new Date(thread.created_at).toLocaleString()}
        </div>
        <div className="mt-6 text-lg text-[#2D3B31] font-light">
          <Markdown>{thread.body}</Markdown>
        </div>

        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <button
            onClick={voteThread}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
              thread.has_voted
                ? "bg-[#F2D8CF] border-[#D96C4A] text-[#C05B3B]"
                : "border-[#EAE5D9] text-[#5C6B5F] hover:border-[#D96C4A] hover:text-[#D96C4A]"
            }`}
            data-testid="thread-detail-upvote"
          >
            <ArrowBigUp size={16} fill={thread.has_voted ? "#C05B3B" : "none"} />
            <span className="text-sm font-medium">{thread.upvotes}</span>
          </button>
          {user && user !== false && user.role === "admin" && (
            <button
              onClick={togglePin}
              className="inline-flex items-center gap-1.5 text-sm text-[#8A948C] hover:text-[#D96C4A] px-3 py-2"
              data-testid="thread-detail-pin"
            >
              {thread.pinned ? <><PinOff size={14} /> Unpin</> : <><Pin size={14} /> Pin</>}
            </button>
          )}
          {user && user !== false && (
            <button
              onClick={() => reportItem("thread", thread.id)}
              className="inline-flex items-center gap-1.5 text-sm text-[#8A948C] hover:text-[#D96C4A] px-3 py-2"
              data-testid="thread-detail-report"
            >
              <Flag size={14} /> Report
            </button>
          )}
          {canDeleteThread && (
            <button
              onClick={deleteThread}
              className="inline-flex items-center gap-1.5 text-sm text-[#8A948C] hover:text-[#C05B3B] px-3 py-2"
              data-testid="thread-detail-delete"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </article>

      <section className="mt-14">
        <div className="text-xs tracking-widest uppercase text-[#D96C4A]">The gathering</div>
        <h2 className="font-serif-display text-3xl font-medium text-[#2D3B31] mt-2 inline-flex items-center gap-3">
          <MessageCircle size={28} color="#D96C4A" />
          {replies.length} {replies.length === 1 ? "reply" : "replies"}
        </h2>

        <div className="mt-8 space-y-4" data-testid="replies-list">
          {replies.map((r) => {
            const canDelete = user && user !== false && (user.role === "admin" || user.id === r.author?.id);
            return (
              <div key={r.id} className="organic-card p-6 md:p-7" data-testid={`reply-item-${r.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-serif-display text-lg bg-[#D96C4A] text-white">
                    {(r.author?.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm flex items-center gap-2 flex-wrap">
                      <span className="text-[#2D3B31] font-medium">{r.author?.name}</span>
                      <span className="text-[#8A948C]"> · {new Date(r.created_at).toLocaleString()}</span>
                      {r.sentiment && <SentimentChip sentiment={r.sentiment} size="xs" />}
                    </div>
                    <div className="mt-2 text-[#2D3B31]">
                      <Markdown>{r.body}</Markdown>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <button
                        onClick={() => voteReply(r.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                          r.has_voted
                            ? "bg-[#F2D8CF] border-[#D96C4A] text-[#C05B3B]"
                            : "border-[#EAE5D9] text-[#5C6B5F] hover:border-[#D96C4A] hover:text-[#D96C4A]"
                        }`}
                        data-testid={`reply-upvote-${r.id}`}
                      >
                        <ArrowBigUp size={14} fill={r.has_voted ? "#C05B3B" : "none"} />
                        {r.upvotes}
                      </button>
                      {user && user !== false && (
                        <button
                          onClick={() => reportItem("reply", r.id)}
                          className="text-xs text-[#8A948C] hover:text-[#D96C4A] inline-flex items-center gap-1"
                          data-testid={`reply-report-${r.id}`}
                        >
                          <Flag size={12} /> Report
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => deleteReply(r.id)}
                          className="text-xs text-[#8A948C] hover:text-[#C05B3B] inline-flex items-center gap-1"
                          data-testid={`reply-delete-${r.id}`}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply form */}
        {user && user !== false ? (
          <form onSubmit={postReply} className="organic-card p-6 md:p-8 mt-8" data-testid="reply-form">
            <label className="text-xs uppercase tracking-widest text-[#8A948C] block mb-2">Add your reflection</label>
            <MarkdownEditor
              value={body}
              onChange={setBody}
              placeholder="Reply with kindness. Markdown formatting is supported."
              testIdPrefix="reply-body"
            />
            <div className="mt-4 flex justify-end">
              <button className="btn-accent inline-flex items-center gap-2" disabled={submitting || !body.trim()} data-testid="reply-submit-btn">
                <Send size={16} />
                {submitting ? "Posting..." : "Post reply"}
              </button>
            </div>
          </form>
        ) : (
          <div className="organic-card p-6 mt-8 text-center">
            <p className="text-[#5C6B5F]">
              <Link to="/login" className="text-[#D96C4A] hover:underline">Sign in</Link> to share your reflection.
            </p>
          </div>
        )}

        {error && <div className="mt-4 text-sm text-[#C05B3B]">{error}</div>}
      </section>
    </div>
  );
}
